import { GridRowsProp } from '@mui/x-data-grid'
import { UsersReportSearchLog, UsersReportSearchLogRow } from '../interfaces'
import dayjs from 'dayjs'

// mapeia tipos de ação para rótulos em pt-BR
export function mapActionType(
  translate: (item: string) => string,
  requestType?: { readonly type?: 'CREATE' | 'EDIT' | 'DELETE' | string }
): string {
  const raw = String(requestType?.type || '').toUpperCase()

  if (raw === 'CREATE' || raw === 'POST') return translate('CREATE')
  if (raw === 'PUT' || raw === 'UPDATE') return translate('EDIT')
  if (raw === 'DELETE' || raw === 'REMOVED') return translate('DELETE')

  return raw ? raw : ''
}

export function buildReportTypeOptions(translate: (item: string) => string) {
  return [
    { value: 'create', label: translate('CREATE') },
    { value: 'edit', label: translate('EDIT') },
    { value: 'activate_deactivate', label: translate('ACTIVATE_DEACTIVATE') },
    { value: 'change_password', label: translate('CHANGE_PASSWORD') },
    { value: 'disconnect', label: translate('DISCONNECT') },
  ]
}

const routeToTypeMap: Record<string, string> = {
  '/user/store': 'create',
  '/user/:id': 'edit',
  '/user/:id/activate-or-deactivate': 'activate_deactivate',
  '/auth/login/change-password': 'change_password',
  '/user/force-disconnection': 'disconnect',
}

export function mapRouteToReportTypeLabel(
  translate: (item: string) => string,
  route: string
): string {
  // Normalize route com parâmetros dinâmicos (substitui números por :id)
  const normalizedRoute = route.replace(/\/\d+/g, '/:id')

  const type = routeToTypeMap[normalizedRoute]
  if (type) {
    const options = buildReportTypeOptions(translate)
    const option = options.find((opt) => opt.value === type)
    return option ? option.label : route
  }
  return route
}

// ---------------------------------------------------------------------------
// INTERNAL HELPERS FOR LABEL MATCHING
// ---------------------------------------------------------------------------

const normalizeUrl = (input: string) =>
  String(input)
    .split(/[?#]/)[0]
    .replace(/(^\/+|\/+$)/g, '')
    .replace(/%20/g, ' ')

// Tenta encontrar o Label legível baseado em uma URL de entrada.
export function findUserLabelByUrl(
  route: string,
  translate: (item: string) => string
): string | undefined {
  if (!route) return undefined
  const incoming = normalizeUrl(route).replace(/\/\d+/g, '/:id')
  if (!incoming) return undefined

  const type = routeToTypeMap[incoming] ?? routeToTypeMap['/' + incoming]
  
  if (type) {
    const options = buildReportTypeOptions(translate)
    const option = options.find((opt) => opt.value === type)
    return option?.label
  }

  return undefined
}

// Função para extrair datas do content
export const extractDatesFromContent = (content: string) => {
  try {
    const parsedContent = JSON.parse(content)
    return {
      start_date: parsedContent.start_date || null,
      finish_date: parsedContent.finish_date || null,
    }
  } catch {
    return {
      start_date: null,
      finish_date: null,
    }
  }
}

// Função dedicada para mapear os dados das linhas, separando responsabilidade
export const mapUsersReportRows = (
  data: readonly UsersReportSearchLog[],
  page: number,
  pageSize: number,
  translate: (key: string) => string
): GridRowsProp => {
  return data.map((element: UsersReportSearchLog, index: number) => {
    const generatedId = element.id ?? page * pageSize + index + 1

    const reportName = element.route
      ? mapRouteToReportTypeLabel(translate, element.route)
      : translate('Unknown Report')

    // action_display agora é derivado apenas da rota (reportName)
    const actionLabel = reportName

    const { start_date: contentStartDate, finish_date: contentFinishDate } =
      element.content
        ? extractDatesFromContent(element.content)
        : { start_date: null, finish_date: null }

    return {
      id: generatedId,
      ...element,
      'user:representation_name': element?.user_name || '',
      report_display: reportName,
      action_display: actionLabel,
      origin: element?.station ? `Estação - ${element?.station}` : '',
      content_start_date: contentStartDate,
      content_finish_date: contentFinishDate,
      content_period:
        contentStartDate || contentFinishDate
          ? `${
              contentStartDate
                ? dayjs(contentStartDate).format('DD/MM/YYYY HH:mm')
                : ''
            }${contentStartDate && contentFinishDate ? ' - ' : ''}${
              contentFinishDate
                ? dayjs(contentFinishDate).format('DD/MM/YYYY HH:mm')
                : ''
            }`
          : null,
    } as UsersReportSearchLogRow
  })
}